/**
=========================================================
* Argon UI Dashboard React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/Argon-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Argonware.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from "@mui/material/Box";

// Argon UI Dashboard React components
import ArgonBox from "components/ArgonBox";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Argon UI Dashboard React context
import { useArgonController } from "context";

function SidenavCollapse({ color, icon, name, children, active, noCollapse, collapseContoller, handleCollapse, collapse, myKey, badgeContent, ...rest }) {
  const [controller] = useArgonController();
  const { miniSidenav, transparentSidenav } = controller;
  const shouldShowBadge = Number(badgeContent) > 0 && !miniSidenav;

  return (
    <>
      <ListItem component="li" onClick={() => collapse && collapse?.length ? collapseContoller === myKey ? handleCollapse(null) : handleCollapse(myKey) : null}>
        <ArgonBox {...rest} sx={(theme) => collapseItem(theme, { active, transparentSidenav })}>
          <ListItemIcon
            sx={(theme) => collapseIconBox(theme, { active, transparentSidenav, color })}
          >
            {typeof icon === "string" ? (
              <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
            ) : (
              icon
            )}
          </ListItemIcon>

          <ListItemText
            primary={name}
            sx={(theme) => collapseText(theme, { miniSidenav, transparentSidenav, active })}/>
          {shouldShowBadge && (
            <Box
              component="span"
              sx={{
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: "10px",
                bgcolor: "#f5365c",
                color: "#fff",
                fontSize: "0.68rem",
                fontWeight: 700,
                lineHeight: "20px",
                textAlign: "center",
                ml: 1,
              }}
            >
              {badgeContent > 99 ? "99+" : badgeContent}
            </Box>
          )}
          {collapse && collapse?.length ? collapseContoller === myKey ? <ExpandLess /> : <ExpandMore /> : null}
        </ArgonBox>
      </ListItem>
      {children && (
        <Collapse in={collapseContoller === myKey} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  color: "info",
  active: false,
  noCollapse: false,
  children: false,
  collapse: [],
  badgeContent: 0,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  children: PropTypes.node,
  active: PropTypes.bool,
  noCollapse: PropTypes.bool,
  handleCollapse: PropTypes.func,
  collapseContoller: PropTypes.string,
  collapse: PropTypes.array,
  myKey: PropTypes.string,
  badgeContent: PropTypes.number,
};

export default SidenavCollapse;
